import React, { useEffect, useState } from "react";
import { Steps, Spin, message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../utils/s3Upload";
import { extractBidOptimizerInput } from "../utils/engineDataExtraction";
import StepUpload, { StepUploadData } from "./StepUpload";
import StepConfiguration, { StepConfigurationData } from "./StepConfiguration";
import StepProcessing from "./StepProcessing";
import StepPowerAnalysis from "./StepPowerAnalysis";
import StepApplyBid from "./StepApplyBid";

// Internal step indices (0-4) vs. the 4 rail steps shown to the user:
// Processing (2) is an interstitial sharing the "Power Analysis" rail slot
// while the job runs, same pattern as causal-loop/components/CreateCausalLoop.tsx.
// This wizard has one fewer rail step than the Synthetic Control variant - no
// "Incrementality Results" step, since an RCT design is a pre-campaign plan
// (Treatment/Control assignment + power analysis), not a post-campaign
// measurement - there's no synthetic-control-style lift computation to show
// after the fact for this variant.
const RAIL_STEPS = [
  { title: "Upload Data", description: "1 file - powers both engines" },
  { title: "Configuration", description: "Bid Optimizer + RCT Test setup" },
  { title: "Power Analysis", description: "RCT 50/50 split results" },
  { title: "Apply Bid", description: "Treatment markets, apply to campaigns" },
];

const railIndexFor = (current: number) => (current <= 1 ? current : current - 1);

const CreateCausalLoopRct: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<number>(location?.state?.step ?? 0);
  const [stepUploadData, setStepUploadData] = useState<StepUploadData | null>(null);
  const [stepConfigurationData, setStepConfigurationData] = useState<StepConfigurationData | null>(null);
  const [jobName, setJobName] = useState<string>(location?.state?.jobName || "");
  const [jobConfigId, setJobConfigId] = useState<string>(location?.state?.jobConfigId || "");
  const [uploadingEarly, setUploadingEarly] = useState(false);
  // Set when opened via "View" from the job list - lands on Configuration
  // (step 1), same entry point as causal-loop/components/CreateCausalLoop.tsx's
  // Synthetic Control variant. Step 2's form is shown read-only from the
  // job's saved config (no file in this browser session to re-detect columns
  // from), and Next skips straight to Power Analysis (Processing only ever
  // submits a job once, at creation).
  const viewOnly: boolean = Boolean(location?.state?.viewOnly);
  const [viewConfigData, setViewConfigData] = useState<StepConfigurationData | null>(null);

  useEffect(() => {
    if (!viewOnly || !jobConfigId || viewConfigData) return;
    axiosInstance
      .get(`/api/jobConfig/${jobConfigId}`)
      .then((res) => {
        const cfg = res.data || {};
        const bm = cfg.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping || {};
        const params = cfg.modelParameterConfig?.parameters || {};
        const mapped: StepConfigurationData = {
          jobName: cfg.name || jobName || "",
          campaignObjective: params.campaignObjective || "sales",
          customerIdentifier: "",
          targetVariable: bm.target_variables?.[0] || "",
          age: bm.age_varialbes?.[0],
          gender: bm.gender_varialbes?.[0],
          devicePlatform: bm.device_platform_varialbes?.[0],
          geoLocationType: params.geoLocationType || "dma",
          region: bm.region_varialbes?.[0] || "",
          locale: bm.locale_varialbes?.[0],
          userOs: bm.user_os_varialbes?.[0],
          dateColumn: params.dateColumn || "",
          durations: params.durations || [],
          weeklyCap: params.weeklyInvestmentCap ?? 0,
          totalWeeklySpend: params.totalWeeklySpend ?? 0,
          isSales: params.isSales ?? true,
        };
        setViewConfigData(mapped);
        setStepConfigurationData(mapped);
        if (!jobName) setJobName(mapped.jobName);
      })
      .catch((err) => {
        // Without a fallback here, a failed fetch leaves viewConfigData null
        // forever and StepConfiguration's "existingData not ready yet" guard
        // shows a permanent loading spinner - see
        // causal-loop/components/CreateCausalLoop.tsx's identical fallback.
        console.error("Error loading saved Causal Loop (RCT) configuration:", err);
        const fallback: StepConfigurationData = {
          jobName: jobName || "",
          campaignObjective: "sales",
          customerIdentifier: "",
          targetVariable: "",
          geoLocationType: "dma",
          region: "",
          dateColumn: "",
          durations: [],
          weeklyCap: 0,
          totalWeeklySpend: 0,
          isSales: true,
        };
        setViewConfigData(fallback);
        setStepConfigurationData(fallback);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOnly, jobConfigId]);

  // Only the Bid Optimizer copy uploads here, right after Step 1 - the RCT
  // copy needs date/location/target columns known first (uploaded by Step
  // 2's own effect once those are picked - see StepConfiguration.tsx).
  const handleUploadNext = async (data: StepUploadData) => {
    setStepUploadData(data);
    if (jobConfigId) {
      setCurrent(1);
      return;
    }
    setUploadingEarly(true);
    try {
      const newJobId = uuidv4();
      await uploadFileToS3(
        extractBidOptimizerInput(data.dataFile),
        `${newJobId}/input/bid_multiplier_data.csv`
      );
      setJobConfigId(newJobId);
      setCurrent(1);
    } catch (err) {
      console.error("Error uploading Causal Loop (RCT) data:", err);
      message.error("Failed to upload your file. Please try again.");
    } finally {
      setUploadingEarly(false);
    }
  };

  // View-mode-only read-only summary for Step 0 (Upload) - there's no file in
  // this browser session to re-preview, so this shows which columns were
  // mapped instead, derived from the same fetched job config Step 1 uses.
  const viewColumns = viewConfigData
    ? ([
        { label: "Date Column", value: viewConfigData.dateColumn },
        { label: "Region / Geo Location", value: viewConfigData.region },
        { label: "Target Variable", value: viewConfigData.targetVariable },
        { label: "Age Range", value: viewConfigData.age },
        { label: "Gender", value: viewConfigData.gender },
        { label: "Device Platform", value: viewConfigData.devicePlatform },
        { label: "Locale", value: viewConfigData.locale },
        { label: "User OS", value: viewConfigData.userOs },
      ].filter((c) => c.value) as { label: string; value: string }[])
    : [];

  const getStepContent = () => {
    if (uploadingEarly) {
      return (
        <div className="text-center py-24">
          <Spin size="large" tip="Uploading your data..." />
        </div>
      );
    }
    switch (current) {
      case 0:
        if (viewOnly && jobConfigId) {
          if (!viewConfigData) {
            return (
              <div className="text-center py-24">
                <Spin size="large" tip="Loading saved configuration..." />
              </div>
            );
          }
          return (
            <StepUpload
              onNext={handleUploadNext}
              viewMode
              viewColumns={viewColumns}
              onPrevious={() => navigate("/causal-loop-rct/list")}
              onViewNext={() => setCurrent(1)}
            />
          );
        }
        return <StepUpload onNext={handleUploadNext} existingData={stepUploadData} />;
      case 1:
        if (viewOnly && jobConfigId) {
          return (
            <StepConfiguration
              stepUploadData={stepUploadData}
              jobConfigId={jobConfigId}
              viewMode
              onNext={(data) => {
                setStepConfigurationData(data);
                setCurrent(3);
              }}
              onPrevious={() => setCurrent(0)}
              onNameChange={setJobName}
              existingData={viewConfigData}
            />
          );
        }
        return stepUploadData && jobConfigId ? (
          <StepConfiguration
            stepUploadData={stepUploadData}
            jobConfigId={jobConfigId}
            onNext={(data) => {
              setStepConfigurationData(data);
              setCurrent(2);
            }}
            onPrevious={() => setCurrent(0)}
            onNameChange={setJobName}
            existingData={stepConfigurationData}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete Step 1 first.</div>
        );
      case 2:
        return stepUploadData && stepConfigurationData && jobConfigId ? (
          <StepProcessing
            jobConfigId={jobConfigId}
            stepConfigurationData={stepConfigurationData}
            onSuccess={() => setCurrent(3)}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete previous steps first.</div>
        );
      case 3:
        return jobConfigId ? (
          <StepPowerAnalysis
            jobConfigId={jobConfigId}
            onPrevious={() => (viewOnly || stepUploadData ? setCurrent(1) : navigate("/causal-loop-rct/list"))}
            onNext={() => setCurrent(4)}
          />
        ) : (
          <div className="text-center p-8 text-gray-500">Please complete previous steps first.</div>
        );
      case 4:
        return jobConfigId ? (
          <StepApplyBid
            jobConfigId={jobConfigId}
            onPrevious={() => setCurrent(3)}
            onNext={() => navigate("/causal-loop-rct/list")}
          />
        ) : null;
      default:
        return null;
    }
  };

  const railCurrent = railIndexFor(current);

  return (
    <div className="w-full h-[calc(100vh-75px)] flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="flex flex-1 h-full overflow-hidden">
        <div className="max-h-[calc(100vh)] overflow-y-auto w-1/4 px-8 py-4 bg-gray-50 border-r">
          <div className="pb-1 pt-2 bg-gray-50 mb-4 px-4">
            <h2
              title={jobName || "Causal Loop (RCT) Study"}
              className="text-2xl font-semibold text-gray-900 max-w-[350px] truncate"
            >
              {jobName || "Causal Loop (RCT) Study"}
            </h2>
          </div>
          <Steps
            direction="vertical"
            current={railCurrent}
            items={RAIL_STEPS.map((step, index) => ({
              title: step.title,
              description: current === 2 && index === 2 ? "Running..." : step.description,
              disabled: true,
              status:
                index < railCurrent ? "finish" : index === railCurrent ? "process" : "wait",
            }))}
            className="[&_.ant-steps-item]:mb-16 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[110px] [&_.ant-steps-item-tail]:!w-[4px] [&_.ant-steps-item-icon]:!text-2xl [&_.ant-steps-item-icon]:!w-10 [&_.ant-steps-item-icon]:!h-10 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
          />
        </div>
        <div className="w-3/4 overflow-y-auto px-10 py-7 pb-24">{getStepContent()}</div>
      </div>
    </div>
  );
};

export default CreateCausalLoopRct;
